import type { PlayTreeNode } from './DiffereStatsPlayTreeDrill';

const DECILE_LABELS = [
  'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'D9', 'D10',
] as const;

const DECILE_TITLES = [
  'Top 10 % (meilleurs à la donne)',
  '10–20 %', '20–30 %', '30–40 %', '40–50 %',
  '50–60 %', '60–70 %', '70–80 %', '80–90 %',
  'Bottom 10 % (moins bons à la donne)',
] as const;

/** Max effectif sur un décile parmi les nœuds (échelle commune liste). */
export function globalDecileScaleMax(nodes: PlayTreeNode[]): number {
  let max = 1;
  for (const node of nodes) {
    for (const c of node.decileCounts ?? []) {
      if (c > max) max = c;
    }
  }
  return max;
}

export default function PlayTreeNodeDeciles({
  node,
  variant = 'inline',
  scaleMax,
}: {
  node: PlayTreeNode;
  /** inline = barres compactes sur une ligne ; stacked = graphique sous la branche */
  variant?: 'inline' | 'stacked';
  /** Échelle Y partagée (max décile sur toutes les lignes visibles). */
  scaleMax?: number;
}) {
  const counts = node.decileCounts ?? [];
  if (counts.length === 0 || counts.every(c => c === 0)) return null;

  const rowMax = Math.max(1, ...counts);
  const chartMax = scaleMax != null && scaleMax > 0 ? scaleMax : rowMax;
  const highlights = node.rankHighlights ?? [];
  const rootClass = `differe-stats-node-deciles differe-stats-node-deciles--${variant}`;

  const rankTags = (
    <div
      className={`differe-stats-rank-tags${highlights.length === 0 && variant === 'inline' ? ' differe-stats-rank-tags--empty' : ''}`}
    >
      {highlights.map(h => (
        <span
          key={h.kind}
          className={`differe-stats-rank-tag differe-stats-rank-tag--${h.kind}`}
          title={`Au moins un joueur de cette branche était classé ${h.label} à la donne`}
        >
          {h.label}
        </span>
      ))}
    </div>
  );

  const chart = (
    <div className="differe-stats-decile-chart" aria-label="Répartition par décile à la donne">
      {counts.map((c, i) => (
        <div
          key={DECILE_LABELS[i]}
          className="differe-stats-decile-slot"
          title={`${DECILE_TITLES[i]} : ${c} joueur(s)`}
        >
          <div className="differe-stats-decile-bar-track">
            <div
              className="differe-stats-decile-bar-fill"
              style={{ height: `${(c / chartMax) * 100}%` }}
            />
          </div>
          {variant === 'stacked' && (
            <>
              <span className="differe-stats-decile-count">{c > 0 ? c : ''}</span>
              <span className="differe-stats-decile-label">{DECILE_LABELS[i]}</span>
            </>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={rootClass}
      onClick={e => e.stopPropagation()}
      role="presentation"
    >
      {variant === 'inline' ? (
        <>
          {rankTags}
          {chart}
        </>
      ) : (
        <>
          {chart}
          {highlights.length > 0 && rankTags}
        </>
      )}
    </div>
  );
}
